import { z } from "zod";
const key = z.string().uuid();
const revision = z.string().regex(/^[a-f0-9]{64}$/);
export const MailboxDraftActionSchema = z.enum(["SAVE", "SEND"]);
export const MailboxDraftAttachmentSchema = z
  .object({
    filename: z.string().min(1).max(255),
    contentType: z.string().min(1).max(128),
    r2Key: z.string().min(1).max(1024),
    size: z
      .number()
      .int()
      .nonnegative()
      .max(10 * 1024 * 1024),
  })
  .strict();
export const MailboxDraftContentSchema = z
  .object({
    to: z.array(z.string().email()).max(10),
    cc: z.array(z.string().email()).max(10),
    bcc: z.array(z.string().email()).max(10),
    subject: z.string().max(200),
    bodyHtml: z.string().max(1024 * 1024),
    bodyText: z
      .string()
      .max(1024 * 1024)
      .optional(),
    attachments: z.array(MailboxDraftAttachmentSchema).max(10),
    inReplyTo: key.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.to.length + value.cc.length + value.bcc.length > 20)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["to"],
        message: "Too many recipients",
      });
    if (
      new TextEncoder().encode(value.bodyHtml).byteLength +
        new TextEncoder().encode(value.bodyText ?? "").byteLength >
      1024 * 1024
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bodyHtml"],
        message: "Message exceeds the body byte limit",
      });
  });
export const MailboxDraftSaveCommandSchema = z
  .object({
    draftId: key.nullable(),
    revision: revision.nullable(),
    content: MailboxDraftContentSchema,
  })
  .strict()
  .refine(
    (v) => (v.draftId === null) === (v.revision === null),
    "Existing drafts require a revision",
  );
export const MailboxDraftSendCommandSchema = z.object({ revision }).strict();
export const MailboxDraftCancelCommandSchema = z
  .object({ action: MailboxDraftActionSchema })
  .strict();
export const MailboxDraftViewSchema = z
  .object({
    id: key,
    revision,
    content: MailboxDraftContentSchema,
    canEdit: z.boolean(),
  })
  .strict();
export const MailboxDraftResponseSchema = z
  .object({ data: MailboxDraftViewSchema })
  .strict();
const attempt = {
  key,
  action: MailboxDraftActionSchema,
  createdAt: z.string().datetime(),
};
export const MailboxDraftAttemptSchema = z.discriminatedUnion("status", [
  z
    .object({ ...attempt, status: z.literal("PENDING"), draftId: z.null() })
    .strict(),
  z.object({ ...attempt, status: z.literal("APPLIED"), draftId: key }).strict(),
  z
    .object({ ...attempt, status: z.literal("CANCELLED"), draftId: z.null() })
    .strict(),
]);
export const MailboxDraftAttemptResultSchema = z
  .object({ attempt: MailboxDraftAttemptSchema })
  .strict();
export const MailboxDraftAttemptLookupSchema = z
  .object({ attempt: MailboxDraftAttemptSchema.nullable() })
  .strict();
export type MailboxDraftContent = z.infer<typeof MailboxDraftContentSchema>;
export type MailboxDraftSaveCommand = z.infer<
  typeof MailboxDraftSaveCommandSchema
>;
export type MailboxDraftAttempt = z.infer<typeof MailboxDraftAttemptSchema>;
export type MailboxDraftAction = z.infer<typeof MailboxDraftActionSchema>;
export const MailboxDispatchCommandSchema = z.object({ revision }).strict();
export const MailboxDispatchResponseSchema = z
  .object({
    data: z
      .object({
        id: key,
        revision,
        status: z.enum([
          "DRAFT",
          "QUEUED",
          "SENDING",
          "SENT",
          "DELIVERED",
          "BOUNCED",
          "FAILED",
          "DELIVERY_UNKNOWN",
        ]),
        canDispatch: z.boolean(),
      })
      .strict(),
  })
  .strict();
